/**
 * AddCredentialModal — sl-bmpq.8.3
 *
 * Extracted from credentials/index.tsx for DRY reuse in:
 * - Settings page (credentials/index.tsx)
 * - Workflow Builder (credential-picker.tsx)
 *
 * Features (sl-bmpq.8.1):
 * - Service dropdown with auto-fill from service definitions
 * - OAuth2 tokenUrl auto-filled and read-only
 * - Setup guide rendered as Markdown with Copy button
 */
import { marked } from 'marked';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SettingsEntityFormDialog } from '../shared/settings-form-dialog';
import * as styles from './index.css';
import {
  type CredentialData,
  type CredentialType,
  type ServiceDefinitionInfo,
  useServiceDefinitions,
} from './use-workspace-credentials';

// =============================================================================
// Type Labels
// =============================================================================

export const TYPE_LABELS: Record<CredentialType, string> = {
  api_key: 'API Key',
  oauth2: 'OAuth 2.0',
  basic_auth: 'Basic Auth',
};

// =============================================================================
// Credential Form State + Hook
// =============================================================================

export interface CredentialFormState {
  apiKey: string;
  headerName: string;
  headerPrefix: string;
  username: string;
  password: string;
  clientId: string;
  clientSecret: string;
  tokenUrl: string;
}

export function useCredentialForm(type: CredentialType) {
  const [state, setState] = useState<CredentialFormState>({
    apiKey: '',
    headerName: '',
    headerPrefix: 'Bearer',
    username: '',
    password: '',
    clientId: '',
    clientSecret: '',
    tokenUrl: '',
  });

  const setField = useCallback(
    (field: keyof CredentialFormState, value: string) => {
      setState(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const canSave = (() => {
    switch (type) {
      case 'api_key':
        return !!state.apiKey.trim();
      case 'basic_auth':
        return !!state.username.trim() && !!state.password.trim();
      case 'oauth2':
        return (
          !!state.clientId.trim() &&
          !!state.clientSecret.trim() &&
          !!state.tokenUrl.trim()
        );
    }
  })();

  const buildData = useCallback((): CredentialData => {
    switch (type) {
      case 'api_key':
        return {
          key: state.apiKey.trim(),
          ...(state.headerName.trim() && {
            headerName: state.headerName.trim(),
          }),
          ...(state.headerPrefix.trim() && {
            headerPrefix: state.headerPrefix.trim(),
          }),
        };
      case 'basic_auth':
        return {
          username: state.username.trim(),
          password: state.password.trim(),
        };
      case 'oauth2':
        return {
          clientId: state.clientId.trim(),
          clientSecret: state.clientSecret.trim(),
          tokenUrl: state.tokenUrl.trim(),
        };
    }
  }, [type, state]);

  return { state, setField, canSave, buildData };
}

// =============================================================================
// Credential Form Fields
// =============================================================================

export function CredentialFormFields({
  type,
  state,
  setField,
  tokenUrlReadOnly = false,
}: {
  type: CredentialType;
  state: CredentialFormState;
  setField: (field: keyof CredentialFormState, value: string) => void;
  tokenUrlReadOnly?: boolean;
}) {
  return (
    <>
      {type === 'api_key' && (
        <>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>API Key</label>
            <input
              className={styles.formInputSecret}
              autoComplete="off"
              value={state.apiKey}
              onChange={e => setField('apiKey', e.target.value)}
              placeholder="sk-..."
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Header Name (optional)</label>
            <input
              className={styles.formInput}
              value={state.headerName}
              onChange={e => setField('headerName', e.target.value)}
              placeholder="Authorization (default)"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Header Prefix (optional)</label>
            <input
              className={styles.formInput}
              value={state.headerPrefix}
              onChange={e => setField('headerPrefix', e.target.value)}
              placeholder="Bearer (default)"
            />
          </div>
        </>
      )}

      {type === 'basic_auth' && (
        <>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Username</label>
            <input
              className={styles.formInput}
              value={state.username}
              onChange={e => setField('username', e.target.value)}
              placeholder="Username"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Password</label>
            <input
              className={styles.formInputSecret}
              autoComplete="off"
              value={state.password}
              onChange={e => setField('password', e.target.value)}
              placeholder="Password"
            />
          </div>
        </>
      )}

      {type === 'oauth2' && (
        <>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Client ID</label>
            <input
              className={styles.formInput}
              value={state.clientId}
              onChange={e => setField('clientId', e.target.value)}
              placeholder="Client ID"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Client Secret</label>
            <input
              className={styles.formInputSecret}
              autoComplete="off"
              value={state.clientSecret}
              onChange={e => setField('clientSecret', e.target.value)}
              placeholder="Client Secret"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Token URL</label>
            {tokenUrlReadOnly ? (
              <div className={styles.fieldReadonly}>{state.tokenUrl}</div>
            ) : (
              <input
                className={styles.formInput}
                value={state.tokenUrl}
                onChange={e => setField('tokenUrl', e.target.value)}
                placeholder="https://oauth.example.com/token"
              />
            )}
          </div>
        </>
      )}
    </>
  );
}

// =============================================================================
// Setup Guide Renderer
// =============================================================================

function getCallbackBaseUrl(): string {
  return typeof window !== 'undefined'
    ? window.location.origin
    : 'https://app.streamlain.de';
}

function SetupGuideRenderer({
  serviceDef,
}: {
  serviceDef: ServiceDefinitionInfo;
}) {
  const guide = serviceDef.byoaSetupGuide;
  const [copied, setCopied] = useState(false);

  const guideWithCallbackUrl = useMemo(() => {
    if (!guide) return '';
    return guide.replace(/\{CALLBACK_URL\}/g, getCallbackBaseUrl());
  }, [guide]);

  const renderedHtml = useMemo(() => {
    if (!guideWithCallbackUrl) return '';
    const html = marked.parse(guideWithCallbackUrl, { async: false }) as string;
    // Sanitize output: strip script tags and event handlers (defense-in-depth for admin-editable guides)
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/\bon\w+\s*=/gi, 'data-blocked=');
  }, [guideWithCallbackUrl]);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(guideWithCallbackUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [guideWithCallbackUrl]);

  if (!guide) return null;

  return (
    <div className={styles.setupGuideSection}>
      <div className={styles.setupGuideHeader}>
        <span className={styles.setupGuideTitle}>Setup Guide</span>
        <button className={styles.copyButton} onClick={() => void handleCopy()}>
          {copied ? 'Copied!' : 'Copy for AI Assistant'}
        </button>
      </div>
      <div
        className={styles.setupGuideContent}
        dangerouslySetInnerHTML={{ __html: renderedHtml }}
      />
    </div>
  );
}

// =============================================================================
// AddCredentialModal (exported)
// =============================================================================

export interface AddCredentialModalProps {
  onClose: () => void;
  onSave: (
    name: string,
    type: CredentialType,
    service: string,
    data: CredentialData
  ) => Promise<void>;
  isSaving: boolean;
  /** Pre-select a service slug (e.g. from Builder context) */
  initialService?: string;
}

export function AddCredentialModal({
  onClose,
  onSave,
  isSaving,
  initialService,
}: AddCredentialModalProps) {
  const { services } = useServiceDefinitions();
  const [name, setName] = useState('');
  const [selectedSlug, setSelectedSlug] = useState(initialService ?? '');
  const [type, setType] = useState<CredentialType>('api_key');
  const [service, setService] = useState(initialService ?? '');
  const { state, setField, canSave, buildData } = useCredentialForm(type);

  // Auto-fill from initialService when services become available
  const initialApplied = useRef(false);
  useEffect(() => {
    if (!initialService || services.length === 0 || initialApplied.current)
      return;
    const def = services.find(s => s.slug === initialService);
    if (!def) return;
    initialApplied.current = true;
    setSelectedSlug(def.slug);
    setService(def.slug);
    setType(def.authType as CredentialType);
    if (def.authType === 'oauth2' && def.oauthConfig?.token_url) {
      setField('tokenUrl', def.oauthConfig.token_url);
    }
    setName(def.name);
  }, [initialService, services, setField]);

  const selectedServiceDef = useMemo(
    () => services.find(s => s.slug === selectedSlug) ?? null,
    [services, selectedSlug]
  );

  const handleServiceChange = useCallback(
    (slug: string) => {
      setSelectedSlug(slug);

      if (slug === '') {
        setService('');
        setType('api_key');
        setField('tokenUrl', '');
        return;
      }

      const def = services.find(s => s.slug === slug);
      if (!def) return;

      setService(def.slug);
      setType(def.authType as CredentialType);

      if (def.authType === 'oauth2' && def.oauthConfig?.token_url) {
        setField('tokenUrl', def.oauthConfig.token_url);
      } else {
        setField('tokenUrl', '');
      }

      if (!name.trim()) {
        setName(def.name);
      }
    },
    [services, name, setField]
  );

  const handleSave = useCallback(async () => {
    await onSave(name.trim(), type, service.trim() || 'custom', buildData());
    onClose();
  }, [name, type, service, buildData, onSave, onClose]);

  const isCustom = selectedSlug === '';

  return (
    <SettingsEntityFormDialog
      open={true}
      onOpenChange={v => {
        if (!v) onClose();
      }}
      title="Add Credential"
      onSave={() => void handleSave()}
      onCancel={onClose}
      saveLabel="Create"
      cancelLabel="Cancel"
      saveDisabled={!name.trim() || !canSave || isSaving}
      saving={isSaving}
      savingLabel="Saving..."
    >
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Service</label>
        <select
          className={styles.formSelect}
          value={selectedSlug}
          onChange={e => handleServiceChange(e.target.value)}
        >
          <option value="">Custom (manual setup)</option>
          {services.map(svc => (
            <option key={svc.slug} value={svc.slug}>
              {svc.name} (
              {TYPE_LABELS[svc.authType as CredentialType] ?? svc.authType})
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Name</label>
        <input
          className={styles.formInput}
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={
            selectedServiceDef
              ? `e.g., ${selectedServiceDef.name} Production`
              : 'e.g., My API Key'
          }
          autoFocus
        />
      </div>

      {isCustom && (
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Type</label>
          <select
            className={styles.formSelect}
            value={type}
            onChange={e => setType(e.target.value as CredentialType)}
          >
            <option value="api_key">API Key</option>
            <option value="basic_auth">Basic Auth</option>
            <option value="oauth2">OAuth 2.0</option>
          </select>
        </div>
      )}

      {!isCustom && (
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Type</label>
          <div className={styles.fieldReadonly}>
            {TYPE_LABELS[type] ?? type}
          </div>
        </div>
      )}

      <CredentialFormFields
        type={type}
        state={state}
        setField={setField}
        tokenUrlReadOnly={!isCustom && type === 'oauth2'}
      />

      {!isCustom &&
        selectedServiceDef?.oauthConfig?.default_scopes &&
        selectedServiceDef.oauthConfig.default_scopes.length > 0 && (
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Required Scopes</label>
            <div className={styles.scopeHint}>
              {selectedServiceDef.oauthConfig.default_scopes.map(scope => (
                <span key={scope} className={styles.scopeChip}>
                  {scope}
                </span>
              ))}
            </div>
          </div>
        )}

      {selectedServiceDef && (
        <SetupGuideRenderer serviceDef={selectedServiceDef} />
      )}
    </SettingsEntityFormDialog>
  );
}
